import Layout from "@/components/layout/Layout";
import PageHero from "@/components/ui/PageHero";
import { Helmet } from "react-helmet-async";
import { Play, Eye, ThumbsUp, Calendar, Youtube } from "lucide-react";
import { STATIC_ASSETS } from "@/lib/imageUtils";
import { Button } from "@/components/ui/button";

import { galleryVideos as videos } from "@/data/galleryVideos";

const VideoGallery = () => {
  return (
    <Layout>
      <Helmet>
        <title>Video Gallery | Rex & Sons RVs</title>
        <meta
          name="description"
          content="Watch RV walkthroughs, event recaps, and adventure inspiration from Rex & Sons RVs on YouTube. 91 videos, 680+ subscribers."
        />
      </Helmet>

      <PageHero
        title="Video Gallery"
        subtitle="Watch RV walkthroughs, events, and more from Rex & Sons RVs"
        backgroundImage={STATIC_ASSETS.heroService1920}
        backgroundImageMobile={STATIC_ASSETS.heroService640}
      />

      {/* Channel Info */}
      <section className="py-8">
        <div className="container text-center">
          <div className="inline-flex items-center gap-3 rounded-full bg-card border px-6 py-3 shadow-xs">
            <img
              src="https://yt3.ggpht.com/ytc/AIdro_mt4bHcGwuuv13RzwuedUfJzXVddrOdrm0SAA_zj0aER8k=s88-c-k-c0x00ffffff-no-rj"
              alt="Rex & Sons RV YouTube"
              width={40}
              height={40}
              decoding="async"
              className="h-10 w-10 rounded-full"
            />
            <div className="text-left">
              <p className="font-heading text-sm font-bold text-foreground">Rex & Sons RV</p>
              <p className="text-xs text-muted-foreground">680 Subscribers • 91 Videos • 462K Views</p>
            </div>
            <Button asChild size="sm" className="ml-2 bg-red-600 hover:bg-red-700 text-white font-heading uppercase text-xs">
              <a href="https://www.youtube.com/channel/UCOMXdujngOKkWVso3syNywA/" target="_blank" rel="noopener noreferrer">
                <Youtube className="mr-1.5 h-3.5 w-3.5" />
                Subscribe
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Video Grid */}
      <section className="pb-16">
        <div className="container">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <a
                key={video.id}
                href={`https://www.youtube.com/watch?v=${video.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-xl border bg-card shadow-xs overflow-hidden flex flex-col hover:shadow-md transition"
              >
                {/* Thumbnail */}
                <div className="relative aspect-video bg-muted overflow-hidden">
                  <img
                    src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
                    alt={video.title}
                    decoding="async"
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-600 text-white shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="h-6 w-6 ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-heading text-sm font-bold text-foreground line-clamp-2 group-hover:text-primary transition">
                    {video.title}
                  </h3>
                  <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2">{video.description}</p>
                  <div className="mt-auto pt-3 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{video.date}</span>
                    <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{video.views}</span>
                    <span className="flex items-center gap-1"><ThumbsUp className="h-3 w-3" />{video.likes}</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary py-10">
        <div className="container text-center">
          <h2 className="font-heading text-2xl font-bold uppercase text-primary-foreground">See More Videos</h2>
          <p className="mt-2 text-primary-foreground/80">Subscribe to our YouTube channel for the latest RV walkthroughs and events.</p>
          <Button asChild size="lg" className="mt-4 bg-red-600 hover:bg-red-700 text-white font-heading uppercase">
            <a href="https://www.youtube.com/channel/UCOMXdujngOKkWVso3syNywA/" target="_blank" rel="noopener noreferrer">
              <Youtube className="mr-2 h-5 w-5" />
              Subscribe on YouTube
            </a>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default VideoGallery;
